/**
 * Aufgabenvorlagen (`task_templates`) - Lesen und Schreiben.
 *
 * ============================== WAS SICH HIER GEAENDERT HAT ==============================
 *
 * Bis zur Vorlagenflaeche (2026-08-12) stand hier NUR `taskVorlagenLaden` mit dem Rueckgabetyp
 * `unknown[]` - mit der ausdruecklichen Begruendung, die Form der Zeilen sei "fuer eine spaetere
 * Oberflaeche zu klaeren" (Task-1-Brief). Diese Oberflaeche ist
 * `components/TaskTemplate/VorlagenDialog.tsx`; die Form ist damit geklaert und steht als
 * `Aufgabenvorlage`/`VorlagenZeile` unten. `state/reduzierer.ts` fuehrt `taskVorlagen` weiterhin als
 * `unknown[]` - ein `Aufgabenvorlage[]` ist dorthin zuweisbar, der Speicher bleibt also unberuehrt.
 *
 * ============================== GEMESSEN, NICHT ANGENOMMEN ==============================
 *
 *   Routen     `GET /api/task-templates` (Rechte `null`, Constraint 9), `POST` (`manage_templates`),
 *              `PATCH /api/task-templates/:templateId` (`manage_templates`), `DELETE` (dito) -
 *              `backend/src/http/endpunktMatrix.ts:125-128`.
 *   POST       `{ name, rows }`, beide Pflicht (400 sonst), Antwort 201 + die Zeile
 *              (`routes/zustaendigkeit.ts:131-154`).
 *
 *   LAND (Auftrag "Taskvorlagen je Land (EU/China) getrennt pflegbar", 2026-09-01): `land: "eu" |
 *   "china"` ist seither ein drittes PFLICHTFELD von `Aufgabenvorlage` UND von `POST` (400 ohne es) -
 *   FESTER VERTRAG aus dem parallel laufenden Backend-Auftrag, hier noch nicht selbst gemessen (der
 *   Vertrag stand zum Zeitpunkt dieser Aenderung als Auftragsvorgabe fest, das Backend dazu lief
 *   GLEICHZEITIG in einem eigenen Worktree). `GET` liefert seither bis zu ZWEI Zeilen - eine
 *   `land: "china"` mit dem heutigen Bestand, eine `land: "eu"` erst NACHDEM sie einmal gespeichert
 *   wurde (vorher schlicht keine EU-Zeile in der Antwort). `PATCH` bleibt UNVERAENDERT (`name`/`rows`,
 *   KEIN `land` - ein Land wechselt seine Vorlage nie nachtraeglich, siehe `VORLAGEN_SCHREIBFELDER`
 *   unten, die weiterhin nur zwei Namen fuehrt).
 *   PATCH      `{ rowVersion, felder }` wie die uebrigen Feldgruppen-PATCH des Hauses. `felder` muss
 *              ein NICHT-LEERES Objekt sein (400 sonst) und darf ausschliesslich `name`/`rows`
 *              enthalten - `VORLAGEN_FELDER` (`backend/src/domaene/schreibbareFelder.ts:153`), sonst
 *              `FeldNichtInGruppeError` (400). Anders als bei `supplier_extras` sind Lese- und
 *              Schreibname hier IDENTISCH: beide Spalten sind einsilbig, `oeffentlich()` laesst sie
 *              unveraendert.
 *   Zeilenform `rows` ist eine jsonb-Liste. Ihre Form ist an ZWEI Stellen bindend und deshalb kein
 *              freies Feld: `uebertrageVerantwortung` (`domaene/zustaendigkeit.ts:115-119`) liest
 *              `zeile->>'id'` und `zeile->>'responsibilityId'` per SQL, um offene Tasks ueber
 *              `order_tasks.template_row_id` einem Modul zuzuordnen. Wer `id` weglaesst oder
 *              `responsibilityId` anders benennt, macht die Uebertragung LAUTLOS wirkungslos - sie
 *              faende schlicht keine Zeile. Die uebrigen Felder stammen aus der Vorlagenzeile des
 *              Bestands (index.html:5495-5502) und wandern ueber die Migration
 *              (`backend/src/migration/aufgaben.ts:81`) unveraendert in dieselbe Spalte.
 */
import { anfrage } from "./basis.ts";

export const BASISPFAD = "/api/task-templates";

/**
 * Eine Zeile einer Vorlage - eine Standardaufgabe, die jede NEU angelegte Bestellung uebernimmt.
 *
 * `id` und `responsibilityId` sind die zwei tragenden Felder (siehe Kopfkommentar, "Zeilenform"):
 * `id` landet als `order_tasks.template_row_id` am erzeugten Task, `responsibilityId` verweist auf
 * `responsibility_modules.id`. Eine leere `responsibilityId` heisst "keinem Modul zugeordnet" - so
 * eine Zeile erzeugt Tasks, die eine spaetere Verantwortungsaenderung NICHT mitzieht.
 */
export interface VorlagenZeile {
  id: string;
  phaseId: string;
  name: string;
  description: string;
  defaultAssignee: string;
  responsibilityId: string;
}

/**
 * Eine Vorlage, wie `GET /api/task-templates` sie liefert (`oeffentlich()`, camelCase).
 *
 * `land` (Auftrag "Taskvorlagen je Land (EU/China) getrennt pflegbar") ist ein PFLICHTFELD -
 * `GET /api/task-templates` liefert je Land HOECHSTENS eine Zeile, niemals eine ohne erkennbares
 * Land (siehe Kopfkommentar, Abschnitt "LAND").
 */
export interface Aufgabenvorlage {
  id: string;
  name: string;
  version: number;
  rows: VorlagenZeile[];
  rowVersion: number;
  land: "eu" | "china";
}

/** Die zwei schreibbaren Spalten - `VORLAGEN_FELDER` (`domaene/schreibbareFelder.ts:153`). */
export const VORLAGEN_SCHREIBFELDER: readonly string[] = Object.freeze(["name", "rows"]);

/**
 * Ein Feldname, den `PATCH /api/task-templates/:templateId` nicht kennt - dieselbe Absicht wie
 * `FeldNichtSchreibbarFehler` in `api/lieferantenExtras.ts`: laut scheitern statt still weglassen
 * oder den Server einen 400 formulieren lassen, dessen Meldung die erlaubten Namen nicht nennt.
 */
export class VorlagenFeldFehler extends Error {
  readonly felder: readonly string[];

  constructor(felder: readonly string[]) {
    const anlass =
      felder.length === 0
        ? "vorlageSchreiben wurde ohne ein einziges Feld aufgerufen - das ist nie eine gueltige " +
          "Absicht (der Server antwortet darauf mit 400)."
        : `vorlageSchreiben: ${felder.map((f) => `"${f}"`).join(", ")} ist kein schreibbares ` +
          "task_templates-Feld.";
    super(
      `${anlass} Erlaubt sind ausschliesslich: ${VORLAGEN_SCHREIBFELDER.join(", ")} ` +
        "(backend/src/domaene/schreibbareFelder.ts).",
    );
    this.name = "VorlagenFeldFehler";
    this.felder = Object.freeze([...felder]);
  }
}

/** Laedt alle Aufgabenvorlagen (der Server sortiert alphabetisch nach `name`). */
export function taskVorlagenLaden(signal?: AbortSignal): Promise<Aufgabenvorlage[]> {
  return anfrage<Aufgabenvorlage[]>(BASISPFAD, { signal });
}

/**
 * Legt eine Vorlage an (201 + die neue Zeile). `land` ist seit dem Land-Auftrag ein drittes
 * Pflichtfeld des POST-Koerpers (400 ohne es, siehe Kopfkommentar, Abschnitt "LAND") - ein Land
 * wechselt seine Vorlage nach dem Anlegen nie mehr, `vorlageSchreiben` unten kennt deshalb bewusst
 * kein `land`-Feld.
 */
export function vorlageAnlegen(
  name: string,
  rows: readonly VorlagenZeile[],
  land: "eu" | "china",
): Promise<Aufgabenvorlage> {
  return anfrage<Aufgabenvorlage>(BASISPFAD, { methode: "POST", koerper: { name, rows, land } });
}

/**
 * Schreibt Felder EINER Vorlage. Wirft `VorlagenFeldFehler` bei einem unbekannten Feldnamen und bei
 * einem LEEREN Objekt (der Server antwortet darauf mit 400, ein Aufruf ohne Feld ist also nie eine
 * gueltige Absicht). Bei 409 wirft `anfrage()` einen `KonfliktFehler` mit dem Serverstand.
 */
export function vorlageSchreiben(
  templateId: string,
  felder: { name?: string; rows?: readonly VorlagenZeile[] },
  rowVersion: number,
): Promise<Aufgabenvorlage> {
  const namen = Object.keys(felder);
  const unbekannt = namen.filter((name) => !VORLAGEN_SCHREIBFELDER.includes(name));
  if (namen.length === 0 || unbekannt.length > 0) {
    throw new VorlagenFeldFehler(unbekannt);
  }
  return anfrage<Aufgabenvorlage>(`${BASISPFAD}/${encodeURIComponent(templateId)}`, {
    methode: "PATCH",
    koerper: { rowVersion, felder },
  });
}
